import type {
  PredictedPosition,
  PredictedStat,
  SanityGameweekRange,
  SanityPredictedPointsBlock
} from './types';
import type {
  Player,
  PlayerFixture,
  PlayerPosition,
  PlayerSortBy
} from '@api-service/players';

// Keep in step with the Studio schema (predictedPointsBlock.ts); the Studio
// validates the count, and the web clamps it as well.
export const MIN_PREDICTED_PLAYERS = 3;
export const MAX_PREDICTED_PLAYERS = 10;
export const DEFAULT_PREDICTED_PLAYERS = 10;

const DEFAULT_PREDICTED_STAT: PredictedStat = 'points';

type PredictedStatConfiguration = {
  label: string;
  sortBy: PlayerSortBy;
  decimals: number;
};

// Points run to whole numbers, so one decimal; the others are mostly below
// one a game and need two to tell players apart.
export const PREDICTED_STATS: Record<
  PredictedStat,
  PredictedStatConfiguration
> = {
  points: { label: 'Predicted points', sortBy: 'predictedPoints', decimals: 1 },
  goals: { label: 'Predicted goals', sortBy: 'predictedGoals', decimals: 2 },
  assists: {
    label: 'Predicted assists',
    sortBy: 'predictedAssists',
    decimals: 2
  },
  cleanSheets: {
    label: 'Predicted clean sheets',
    sortBy: 'predictedCleanSheets',
    decimals: 2
  },
  returns: {
    label: 'Predicted returns',
    sortBy: 'predictedReturns',
    decimals: 2
  }
};

const POSITION_PLURALS: Record<PlayerPosition, string> = {
  GK: 'goalkeepers',
  DEF: 'defenders',
  MID: 'midfielders',
  FWD: 'forwards'
};

export type GameweekWindow = { minGameweek: number; maxGameweek: number };

export type PredictedPlayerRow = {
  player: Player;
  value: number;
  fixtures: PlayerFixture[];
};

// Sanity stores whatever string an older schema allowed, so values are checked
// against the known keys rather than trusted.
const isPredictedStat = (stat: unknown): stat is PredictedStat =>
  typeof stat === 'string' && Object.keys(PREDICTED_STATS).includes(stat);

const isPlayerPosition = (
  position: PredictedPosition | undefined
): position is PlayerPosition =>
  typeof position === 'string' &&
  Object.keys(POSITION_PLURALS).includes(position);

export const resolvePredictedStat = (
  stat: SanityPredictedPointsBlock['stat']
): PredictedStat => (isPredictedStat(stat) ? stat : DEFAULT_PREDICTED_STAT);

/** `undefined` for "All players", which the API reads as no filter. */
export const resolvePredictedPosition = (
  position: SanityPredictedPointsBlock['position']
): PlayerPosition | undefined =>
  isPlayerPosition(position) ? position : undefined;

export const clampPredictedPlayerCount = (count: number | undefined): number =>
  Math.min(
    Math.max(
      Math.trunc(count ?? DEFAULT_PREDICTED_PLAYERS),
      MIN_PREDICTED_PLAYERS
    ),
    MAX_PREDICTED_PLAYERS
  );

export const hasFixedGameweeks = (
  range: SanityGameweekRange | undefined
): boolean =>
  range?.minGameweek !== undefined || range?.maxGameweek !== undefined;

/**
 * The editor's gameweeks, where one end alone means that single gameweek;
 * with neither end, the next gameweek. Null when there is nothing to fall
 * back to, such as after the season.
 */
export const resolvePredictionWindow = (
  range: SanityGameweekRange | undefined,
  nextGameweek: number | null | undefined
): GameweekWindow | null => {
  const from = range?.minGameweek ?? range?.maxGameweek ?? nextGameweek;
  const to = range?.maxGameweek ?? range?.minGameweek ?? nextGameweek;
  if (!from || !to) return null;
  return { minGameweek: Math.min(from, to), maxGameweek: Math.max(from, to) };
};

export const describePredictionWindow = ({
  minGameweek,
  maxGameweek
}: GameweekWindow): string =>
  minGameweek === maxGameweek
    ? `GW${minGameweek}`
    : `GW${minGameweek}-${maxGameweek}`;

export const predictedPointsTitle = (
  block: SanityPredictedPointsBlock
): string => {
  const editorTitle = block.title?.trim();
  if (editorTitle) return editorTitle;
  const { label } = PREDICTED_STATS[resolvePredictedStat(block.stat)];
  const position = resolvePredictedPosition(block.position);
  return position ? `${label}: ${POSITION_PLURALS[position]}` : label;
};

const isInWindow = (fixture: PlayerFixture, gameweekWindow: GameweekWindow) =>
  fixture.gameweek >= gameweekWindow.minGameweek &&
  fixture.gameweek <= gameweekWindow.maxGameweek;

/**
 * Each player's total over the window, highest first. A player with nothing
 * predicted (a blank gameweek, or out injured) is left out rather than shown
 * at zero.
 */
export const buildPredictedRows = (
  players: Player[],
  stat: PredictedStat,
  gameweekWindow: GameweekWindow,
  count: number
): PredictedPlayerRow[] =>
  players
    .map((player) => {
      const fixtures = player.fixtures.filter((fixture) =>
        isInWindow(fixture, gameweekWindow)
      );
      const value = fixtures.reduce(
        (total, fixture) => total + fixture.predictions[stat],
        0
      );
      return { player, value, fixtures };
    })
    .filter((row) => row.value > 0)
    .sort((first, second) => second.value - first.value)
    .slice(0, count);

/** "BOU (H)" for one gameweek, both opponents in a double; a count otherwise. */
export const describeRowFixtures = (
  fixtures: PlayerFixture[],
  gameweekWindow: GameweekWindow
): string => {
  if (gameweekWindow.minGameweek !== gameweekWindow.maxGameweek) {
    return `${fixtures.length} ${
      fixtures.length === 1 ? 'fixture' : 'fixtures'
    }`;
  }
  return fixtures
    .map(
      (fixture) =>
        `${fixture.opponent.shortName} (${fixture.isHome ? 'H' : 'A'})`
    )
    .join(', ');
};
